import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, API_BASE_URL } from '../../services/http.js';
import { ErrorRetryCard } from '../../components/common/ErrorRetryCard.jsx';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../../config/routes.js';
import * as XLSX from 'xlsx';
import ContentReportsTab from './ContentReportsTab.jsx';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s).replace('T', ' ').slice(0, 16);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '');
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtBRL(n) {
  return Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function planLabel(plan) {
  const map = { basic: 'Básico', regular: 'Regular', premium: 'Premium', trial: 'Trial' };
  return map[plan] || plan || '—';
}

function cycleLabel(c) {
  return c === 'annual' ? 'Anual' : c === 'quarterly' ? 'Trimestral' : c || '—';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accent }) {
  return (
    <div className={`rounded-2xl border p-4 bg-surface dark:border-outline flex flex-col gap-1 ${accent ? 'border-primary/30' : ''}`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">{label}</p>
      <p className={`text-2xl font-bold leading-none ${accent ? 'text-primary' : 'text-on-surface '}`}>{value ?? '—'}</p>
      {sub && <p className="text-[11px] text-on-surface-variant mt-0.5">{sub}</p>}
    </div>
  );
}

function MiniBar({ data, valueKey, labelKey, color = 'bg-primary' }) {
  if (!data || data.length === 0) return <p className="text-xs text-on-surface-variant py-4">Sem dados.</p>;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="flex items-end gap-1 h-16 w-full">
      {data.map((d, i) => {
        const h = Math.max(2, Math.round(((d[valueKey] || 0) / max) * 56));
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
            <div className={`w-full rounded-t ${color} opacity-70 group-hover:opacity-100 transition-opacity`} style={{ height: h }} />
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-slate-800 text-on-primary text-[9px] px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
              {d[labelKey]}: {d[valueKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DataTable({ columns, rows, emptyMsg = 'Nenhum dado.', sortKey, sortDirection, onSort }) {
  return (
    <div className="rounded-xl border dark:border-outline overflow-hidden text-sm">
      <div
        className="grid bg-surface-container text-[10px] font-bold uppercase tracking-wider text-on-surface-variant px-3 py-2 gap-2"
        style={{ gridTemplateColumns: columns.map(c => c.width || '1fr').join(' ') }}
      >
        {columns.map(c => (
          <div key={c.key} 
            className={`${c.right ? 'text-right' : ''} ${onSort && c.sortable !== false ? 'cursor-pointer hover:text-on-surface-variant dark:hover:text-slate-200 flex items-center gap-1 ' + (c.right ? 'justify-end' : '') : ''}`} 
            onClick={() => onSort && c.sortable !== false && onSort(c.key)}
          >
            {c.label}
            {sortKey === c.key && (
              <span className="material-symbols-outlined text-[12px]">{sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>
            )}
          </div>
        ))}
      </div>
      <div className="divide-y dark:divide-slate-800 max-h-[55vh] overflow-y-auto">
        {rows.length === 0
          ? <div className="px-3 py-6 text-xs text-on-surface-variant">{emptyMsg}</div>
          : rows.map((row, i) => (
            <div
              key={row._key ?? i}
              className="grid px-3 py-2 gap-2 items-center hover:bg-surface-container dark:hover:bg-slate-800/50 transition-colors"
              style={{ gridTemplateColumns: columns.map(c => c.width || '1fr').join(' ') }}
            >
              {columns.map(c => (
                <div key={c.key} className={`truncate text-xs ${c.right ? 'text-right font-semibold' : 'text-on-surface '}`} title={String(row[c.key] ?? '')}>
                  {row[c.key] ?? '—'}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}

function Badge({ text, color }) {
  const colors = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
    yellow: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
    gray: 'bg-slate-100 text-on-surface-variant  dark:text-on-surface-variant',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${colors[color] || colors.gray}`}>
      {text}
    </span>
  );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'resumo', label: 'Resumo' },
  { id: 'usuarios', label: 'Usuários' },
  { id: 'assinaturas', label: 'Assinaturas' },
  { id: 'consultas', label: 'Consultas' },
  { id: 'conversas', label: 'Conversas' },
  { id: 'mailing', label: 'Mailing' },
  { id: 'optinout', label: 'Opt-in/Opt-out' },
  { id: 'denuncias', label: 'Denúncias' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('resumo');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // --- Resumo state ---
  const [summary, setSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [subsSummary, setSubsSummary] = useState(null);

  // --- Usuários state ---
  const [usersDetail, setUsersDetail] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersDetailError, setUsersDetailError] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  // --- Assinaturas state (reuse usersDetail) ---

  // --- Consultas state ---
  const [queriesByDay, setQueriesByDay] = useState([]);
  const [queriesByUser, setQueriesByUser] = useState([]);
  const [queriesError, setQueriesError] = useState('');
  const [queriesDaySort, setQueriesDaySort] = useState({ key: 'date', dir: 'desc' });
  const [queriesUserSort, setQueriesUserSort] = useState({ key: 'queries', dir: 'desc' });
  const [queriesSelectedDate, setQueriesSelectedDate] = useState('');
  const [queriesSelectedUser, setQueriesSelectedUser] = useState('');

  // --- Sorting state ---
  const [usersSort, setUsersSort] = useState({ key: 'created_at', dir: 'desc' });
  const [convSort, setConvSort] = useState({ key: 'conversation_created_at', dir: 'desc' });
  const [subsSort, setSubsSort] = useState({ key: 'email', dir: 'asc' });

  // --- Mailing & Optin state ---
  const [mailingSubscribers, setMailingSubscribers] = useState([]);
  const [mailingCampaigns, setMailingCampaigns] = useState([]);
  const [mailingSubject, setMailingSubject] = useState('');
  const [mailingContent, setMailingContent] = useState('');
  const [mailingTarget, setMailingTarget] = useState('all');
  const [mailingEmails, setMailingEmails] = useState('');
  const [mailingSubSort, setMailingSubSort] = useState({ key: 'created_at', dir: 'desc' });
  const [mailingLoading, setMailingLoading] = useState(false);
  const [optinLoading, setOptinLoading] = useState(false);


  // --- Conversas state ---
  const [convUsers, setConvUsers] = useState([]);
  const [convUsersError, setConvUsersError] = useState('');
  const [convSelectedUser, setConvSelectedUser] = useState(null);
  const [convDropOpen, setConvDropOpen] = useState(false);
  const [convUserSearch, setConvUserSearch] = useState('');
  const [convFrom, setConvFrom] = useState('');
  const [convTo, setConvTo] = useState('');
  const [convQuery, setConvQuery] = useState('');
  const [convData, setConvData] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState('');
  const [convSelected, setConvSelected] = useState(new Set());
  const convDropRef = useRef(null);

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadResume = useCallback(async () => {
    try {
      const [metricsRes, overviewRes, subsRes] = await Promise.all([
        apiClient.getAdminMetrics(),
        apiClient.getAdminMetricsOverview(),
        apiClient.getAdminSubscriptionsSummary(),
      ]);
      if (metricsRes.res.ok) setSummary(metricsRes.data?.summary ?? null);
      if (overviewRes.res.ok) setOverview(overviewRes.data ?? null);
      if (subsRes.res.ok) setSubsSummary(subsRes.data ?? null);
    } catch {
      setError('Erro ao carregar resumo.');
    }
  }, []);

  const loadQueries = useCallback(async () => {
    setQueriesError('');
    try {
      const [byDay, byUser] = await Promise.all([
        apiClient.getAdminMetricsQueries({ group_by: 'day' }),
        apiClient.getAdminMetricsQueries({ group_by: 'user' }),
      ]);
      if (byDay.res.ok) setQueriesByDay(byDay.data?.data ?? []);
      if (byUser.res.ok) setQueriesByUser(byUser.data?.data ?? []);
      if (!byDay.res.ok && !byUser.res.ok) setQueriesError('Erro ao carregar consultas.');
    } catch {
      setQueriesError('Erro ao conectar ao servidor.');
    }
  }, []);

  const loadMailingData = useCallback(async () => {
    if (activeTab === 'mailing') {
      const res = await apiClient.getAdminMailingCampaigns();
      if (res.res.ok) setMailingCampaigns(res.data?.items ?? []);
    } else if (activeTab === 'optinout') {
      setOptinLoading(true);
      const res = await apiClient.getAdminMailingSubscribers();
      if (res.res.ok) setMailingSubscribers(res.data?.items ?? []);
      setOptinLoading(false);
    }
  }, [activeTab]);


  const loadUsersDetail = useCallback(async () => {
    setUsersLoading(true);
    setUsersDetailError('');
    try {
      const res = await apiClient.getAdminUsers({ detail: true });
      if (res.res.ok) {
        setUsersDetail(res.data?.data ?? []);
      } else {
        setUsersDetailError('Erro ao carregar lista de usuários.');
      }
    } catch {
      setUsersDetailError('Erro ao conectar ao servidor.');
    }
    setUsersLoading(false);
  }, []);

  const loadConvUsers = useCallback(async () => {
    setConvUsersError('');
    try {
      const res = await apiClient.getAdminUsers();
      if (res.res.ok) {
        setConvUsers(res.data?.data ?? []);
      } else {
        setConvUsersError('Erro ao carregar usuários para filtro.');
      }
    } catch {
      setConvUsersError('Erro de conexão ao carregar usuários.');
    }
  }, []);

  const loadConversas = useCallback(async () => {
    setConvLoading(true);
    setConvError('');
    try {
      const res = await apiClient.getAdminConversations({
        user_id: convSelectedUser?.id || undefined,
        user_query: convUserSearch.trim() || undefined,
        from: convFrom.trim() || undefined,
        to: convTo.trim() || undefined,
        conversation_query: convQuery.trim() || undefined,
        limit: 1000,
      });
      if (!res.res.ok) {
        setConvError(res.data?.detail || 'Erro ao carregar conversas.');
        setConvData([]);
      } else {
        setConvData(res.data?.data ?? []);
        setConvSelected(new Set());
      }
    } catch {
      setConvError('Erro ao carregar conversas.');
    }
    setConvLoading(false);
  }, [convSelectedUser, convUserSearch, convFrom, convTo, convQuery]);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      await Promise.all([loadResume(), loadQueries()]);
      setLoading(false);
    };
    run();
  }, []);

  useEffect(() => {
    if (activeTab === 'usuarios' || activeTab === 'assinaturas') {
      if (usersDetail.length === 0) loadUsersDetail();
    }
    if (activeTab === 'conversas') {
      loadConvUsers();
      loadConversas();
    }
    if (activeTab === 'mailing' || activeTab === 'optinout') {
      loadMailingData();
    }
  }, [activeTab]);

  useEffect(() => {
    const handler = (e) => {
      if (convDropRef.current && !convDropRef.current.contains(e.target)) setConvDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────────

  const filteredUsers = useMemo(() => {
    let list = usersDetail;
    if (planFilter) list = list.filter(u => (u.plan_id || 'sem_plano') === planFilter);
    if (userFilter.trim()) {
      const q = userFilter.toLowerCase();
      list = list.filter(u => (u.email || '').toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q));
    }
    return list;
  }, [usersDetail, planFilter, userFilter]);

  const convFilteredUsers = useMemo(() => {
    const q = (convUserSearch || '').trim().toLowerCase();
    if (!q) return convUsers;
    return convUsers.filter(u => (u.email || '').toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q));
  }, [convUsers, convUserSearch]);

  const activeSubs = useMemo(() => usersDetail.filter(u => u.subscription_status === 'active'), [usersDetail]);


  const trend7 = overview?.trend_last_7_days ?? [];
  const donByStatus = overview?.donations_by_status ?? [];

  // --- Sorting Helpers ---
  const handleSort = (key, state, setter) => {
    if (state.key === key) {
      setter({ key, dir: state.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setter({ key, dir: 'asc' });
    }
  };

  const sortData = (data, { key, dir }) => {
    return [...data].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];
      
      const isDateA = typeof valA === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(valA);
      const isDateB = typeof valB === 'string' && /^\d{2}\/\d{2}\/\d{4}/.test(valB);
      
      if (isDateA || isDateB) {
         const parse = (s) => {
            if (typeof s !== 'string') return 0;
            const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}):(\d{2}))?/);
            if (m) return new Date(m[3], m[2]-1, m[1], m[4]||0, m[5]||0).getTime();
            return 0;
         };
         valA = parse(valA);
         valB = parse(valB);
      } else {
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
      }

      if (valA < valB) return dir === 'asc' ? -1 : 1;
      if (valA > valB) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  };


  // ── Export helpers ─────────────────────────────────────────────────────────

  const escapeMd = s => String(s ?? '').replace(/\r?\n/g, ' ').replace(/\|/g, ' ').trim();

  const rowKey = r => `${r.message_id}-${r.conversation_id}-${r.message_created_at}`;
  const today = () => new Date().toISOString().slice(0, 10);
  const download = (blob, name) => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href); };

  const exportConvMD = useCallback(() => {
    const keys = convSelected.size ? Array.from(convSelected) : convData.map(r => rowKey(r));
    const rows = convData.filter(r => keys.includes(rowKey(r)));
    const hdr = ['Nome', 'E-mail', 'Data conversa', 'Título', 'Tipo', 'Conteúdo', 'Data msg'];
    const body = rows.map(r => `| ${[escapeMd(r.user_name), escapeMd(r.user_email), fmt(r.conversation_created_at), escapeMd(r.conversation_title), r.message_role === 'user' ? 'Pergunta' : 'Resposta', escapeMd(r.message_content), fmt(r.message_created_at)].join(' | ')} |`);
    const md = `# Conversas exportadas\n\n| ${hdr.join(' | ')} |\n| ${hdr.map(() => '---').join(' | ')} |\n${body.join('\n')}\n`;
    download(new Blob([md], { type: 'text/markdown;charset=utf-8' }), `conversas-${today()}.md`);
  }, [convData, convSelected]);

  const exportConvXLSX = useCallback(() => {
    const keys = convSelected.size ? Array.from(convSelected) : convData.map(r => rowKey(r));
    const rows = convData.filter(r => keys.includes(rowKey(r)));
    const data = rows.map(r => ({ Nome: r.user_name, 'E-mail': r.user_email, 'Data conversa': fmt(r.conversation_created_at), Título: r.conversation_title, Tipo: r.message_role === 'user' ? 'Pergunta' : 'Resposta', Conteúdo: r.message_content, 'Data msg': fmt(r.message_created_at) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Conversas');
    XLSX.writeFile(wb, `conversas-${today()}.xlsx`);
  }, [convData, convSelected]);

  // ── Tiny utils ─────────────────────────────────────────────────────────────



  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-surface-container  text-on-surface  font-sans">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center gap-4 px-4 sm:px-8 h-14 bg-surface border-b dark:border-outline shadow-sm">
        <button
          onClick={() => navigate(DEFAULT_AUTHENTICATED_ROUTE)}
          className="flex items-center gap-1.5 text-sm text-on-surface-variant hover:text-on-surface dark:hover:text-slate-200 transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Início
        </button>
        <div className="h-4 w-px bg-surface-container-highest" />
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[20px] text-primary">monitoring</span>
          <h1 className="font-bold text-base">Painel Admin</h1>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => { setLoading(true); Promise.all([loadResume(), loadQueries()]).then(() => setLoading(false)); }}
          className="flex items-center gap-1.5 text-xs text-on-surface-variant hover:text-on-surface dark:hover:text-slate-200 transition-colors"
          title="Atualizar dados"
        >
          <span className="material-symbols-outlined text-[16px]">refresh</span>
          Atualizar
        </button>
      </header>

      {loading && (
        <div className="flex items-center justify-center h-64 text-sm text-on-surface-variant gap-2">
          <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
          Carregando dados...
        </div>
      )}

      {error && !loading && (
        <div className="m-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">{error}</div>
      )}

      {!loading && !error && (
        <div className="px-4 sm:px-8 py-6 space-y-6 max-w-screen-xl mx-auto">
          {/* Tabs */}
          <nav className="flex gap-1 flex-wrap border-b dark:border-outline pb-0 -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-on-surface-variant hover:text-on-surface dark:hover:text-slate-300 hover:bg-surface-variant'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          {/* ── RESUMO ── */}
          {activeTab === 'resumo' && (
            <div className="space-y-6">
              {/* KPI grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                <KpiCard label="Total usuários" value={summary?.users ?? '—'} accent />
                <KpiCard label="Assinaturas ativas" value={subsSummary?.active_total ?? '—'} />
                <KpiCard label="Acessos hoje" value={summary?.accesses_today ?? '—'} />
                <KpiCard label="Acessos semana" value={summary?.accesses_week ?? '—'} />
                <KpiCard label="Acessos mês" value={summary?.accesses_month ?? '—'} />
                <KpiCard label="Consultas hoje" value={summary?.queries_today ?? '—'} accent />
                <KpiCard label="Tokens hoje" value={(summary?.tokens_today ?? 0).toLocaleString('pt-BR')} />
                {overview?.today && (
                  <>
                    <KpiCard label="Novos usuários hoje" value={overview.today.new_users ?? '—'} />
                  </>
                )}
                {donByStatus.find(d => d.status === 'confirmada') && (
                  <KpiCard label="Doações confirmadas" value={fmtBRL(donByStatus.find(d => d.status === 'confirmada')?.amount)} sub={`${donByStatus.find(d => d.status === 'confirmada')?.count} transações`} accent />
                )}
              </div>

              {/* 7-day trend */}
              {trend7.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="rounded-2xl border dark:border-outline bg-surface p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Consultas — últimos 7 dias</p>
                    <MiniBar data={[...trend7].reverse()} valueKey="chat_queries" labelKey="date" color="bg-primary" />
                    <div className="flex justify-between text-[10px] text-on-surface-variant mt-2">
                      {[...trend7].reverse().map((d, i) => <span key={i} className="-rotate-45 origin-top-left translate-y-2">{fmtDate(d.date).slice(0,5)}</span>)}
                    </div>
                  </div>
                  <div className="rounded-2xl border dark:border-outline bg-surface p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Acessos — últimos 7 dias</p>
                    <MiniBar data={[...trend7].reverse()} valueKey="total_accesses" labelKey="date" color="bg-blue-500" />
                    <div className="flex justify-between text-[10px] text-on-surface-variant mt-2">
                      {[...trend7].reverse().map((d, i) => <span key={i} className="-rotate-45 origin-top-left translate-y-2">{fmtDate(d.date).slice(0,5)}</span>)}
                    </div>
                  </div>
                  <div className="rounded-2xl border dark:border-outline bg-surface p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Tokens — últimos 7 dias</p>
                    <MiniBar data={[...trend7].reverse()} valueKey="total_tokens" labelKey="date" color="bg-amber-500" />
                    <div className="flex justify-between text-[10px] text-on-surface-variant mt-2">
                      {[...trend7].reverse().map((d, i) => <span key={i} className="-rotate-45 origin-top-left translate-y-2">{fmtDate(d.date).slice(0,5)}</span>)}
                    </div>
                  </div>
                </div>
              )}

              {/* Doações por status */}
              {donByStatus.length > 0 && (
                <div className="rounded-2xl border dark:border-outline bg-surface p-4 space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Doações por status</p>
                  <div className="flex flex-wrap gap-3">
                    {donByStatus.map(d => (
                      <div key={d.status} className="rounded-xl border dark:border-outline px-4 py-3 flex flex-col gap-0.5 min-w-[120px]">
                        <span className="text-[10px] uppercase font-bold text-on-surface-variant">{d.status}</span>
                        <span className="text-lg font-bold">{fmtBRL(d.amount)}</span>
                        <span className="text-[11px] text-on-surface-variant">{d.count} transações</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── USUÁRIOS ── */}
          {activeTab === 'usuarios' && (
            <div className="space-y-6">
              {usersDetailError ? (
                <ErrorRetryCard message={usersDetailError} onRetry={loadUsersDetail} />
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-surface p-4 rounded-xl border border-outline-variant/30">
                <div>
                  <label className="block text-[10px] font-bold uppercase text-on-surface-variant mb-1">Buscar</label>
                  <input
                    value={userFilter}
                    onChange={e => setUserFilter(e.target.value)}
                    placeholder="Nome ou e-mail..."
                    className="rounded-lg border dark:border-outline  px-3 py-2 text-sm w-56"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-on-surface-variant mb-1">Plano</label>
                  <select
                    value={planFilter}
                    onChange={e => setPlanFilter(e.target.value)}
                    className="rounded-lg border dark:border-outline  px-3 py-2 text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="basic">Básico</option>
                    <option value="regular">Regular</option>
                    <option value="premium">Premium</option>
                    <option value="trial">Trial</option>
                    <option value="sem_plano">Sem plano</option>
                  </select>
                </div>
                <button
                  onClick={loadUsersDetail}
                  disabled={usersLoading}
                  className="px-4 py-2 rounded-lg bg-primary text-on-primary dark:text-on-surface text-sm font-medium disabled:opacity-50"
                >
                  {usersLoading ? 'Carregando...' : 'Atualizar'}
                </button>
                <span className="text-xs text-on-surface-variant ml-auto">{filteredUsers.length} usuários</span>
              </div>

              <DataTable
                sortKey={usersSort.key}
                sortDirection={usersSort.dir}
                onSort={(k) => handleSort(k, usersSort, setUsersSort)}
                columns={[
                  { key: 'email', label: 'E-mail', width: '2fr' },
                  { key: 'full_name', label: 'Nome', width: '1.5fr' },
                  { key: 'plan_badge', label: 'Plano', width: '90px' },
                  { key: 'billing_cycle', label: 'Ciclo', width: '90px' },
                  { key: 'credits_info', label: 'Créditos/semana', width: '100px', right: true },
                  { key: 'total_queries', label: 'Consultas', width: '80px', right: true },
                  { key: 'verified_badge', label: 'Email', width: '60px' },
                  { key: 'created_at', label: 'Cadastro', width: '100px' },
                  { key: 'last_access', label: 'Último acesso', width: '120px' },
                ]}
                rows={sortData(filteredUsers.map(u => ({
                  _key: u.id,
                  email: u.email,
                  full_name: u.full_name || '—',
                  plan_badge: u.plan_id ? planLabel(u.plan_id) : '—',
                  billing_cycle: cycleLabel(u.billing_cycle),
                  credits_info: u.credits_per_week ? `${u.credits_consumed}/${u.credits_per_week}` : '—',
                  total_queries: u.total_queries || 0,
                  verified_badge: u.email_verified ? '✓' : '✗',
                  created_at: fmtDate(u.created_at),
                  last_access: u.last_access ? fmt(u.last_access) : '—',
                })), usersSort)}
              />
              </>
              )}
            </div>
          )}

          {/* ── ASSINATURAS ── */}
          {activeTab === 'assinaturas' && (
            <div className="space-y-6">
              {/* Summary cards */}
              {subsSummary && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <KpiCard label="Total ativas" value={subsSummary.active_total} accent />
                    {['basic', 'regular', 'premium'].map(plan => {
                      const rows = (subsSummary.by_plan || []).filter(r => r.plan_id === plan && r.status === 'active');
                      const count = rows.reduce((s, r) => s + r.count, 0);
                      return <KpiCard key={plan} label={planLabel(plan)} value={count} />;
                    })}
                  </div>

                  <div className="rounded-2xl border dark:border-outline bg-surface p-4 space-y-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Breakdown por plano e status</p>
                    <DataTable
                      columns={[
                        { key: 'plan', label: 'Plano', width: '1fr' },
                        { key: 'cycle', label: 'Ciclo', width: '1fr' },
                        { key: 'status', label: 'Status', width: '1fr' },
                        { key: 'count', label: 'Qtd.', width: '80px', right: true },
                        { key: 'total_credits', label: 'Créditos/semana', width: '110px', right: true },
                      ]}
                      rows={(subsSummary.by_plan || []).map((r, i) => ({
                        _key: i,
                        plan: planLabel(r.plan_id),
                        cycle: cycleLabel(r.billing_cycle),
                        status: r.status,
                        count: r.count,
                        total_credits: r.total_credits,
                      }))}
                    />
                  </div>
                </div>
              )}

              {/* Active subscriptions list */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Assinaturas ativas ({activeSubs.length})</p>
                  <button onClick={loadUsersDetail} disabled={usersLoading} className="text-xs text-on-surface-variant hover:text-on-surface dark:hover:text-slate-200">
                    {usersLoading ? 'Carregando...' : 'Atualizar lista'}
                  </button>
                </div>
                <DataTable
                  columns={[
                    { key: 'email', label: 'E-mail', width: '2fr' },
                    { key: 'full_name', label: 'Nome', width: '1.5fr' },
                    { key: 'plan', label: 'Plano', width: '90px' },
                    { key: 'cycle', label: 'Ciclo', width: '90px' },
                    { key: 'credits', label: 'Créditos/semana', width: '110px', right: true },
                    { key: 'period_end', label: 'Renova em', width: '110px' },
                  ]}
                  rows={activeSubs.map(u => ({
                    _key: u.id,
                    email: u.email,
                    full_name: u.full_name || '—',
                    plan: planLabel(u.plan_id),
                    cycle: cycleLabel(u.billing_cycle),
                    credits: u.credits_per_week || '—',
                    period_end: fmtDate(u.period_end),
                  }))}
                  emptyMsg="Nenhuma assinatura ativa. Clique em 'Atualizar lista'."
                />
              </div>
            </div>
          )}

          {/* ── CONSULTAS ── */}
          {activeTab === 'consultas' && (
            <div className="space-y-6">
              {queriesError ? (
                <ErrorRetryCard message={queriesError} onRetry={loadQueries} />
              ) : (
                <>
                  {queriesByDay.length > 0 && (
                <div className="rounded-2xl border dark:border-outline bg-surface p-4 space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Consultas por dia (últimas {Math.min(30, queriesByDay.length)} entradas)</p>
                  <MiniBar data={queriesByDay.slice(-30)} valueKey="queries" labelKey="date" color="bg-primary" />
                </div>
              )}
              <div className="flex justify-end gap-2 mb-2">
                  {(queriesSelectedDate || queriesSelectedUser) && (
                     <button onClick={() => {setQueriesSelectedDate(''); setQueriesSelectedUser('');}} className="text-xs px-3 py-1 rounded bg-surface-container-highest text-on-surface-variant ">
                        Limpar Filtros
                     </button>
                  )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    Por dia {queriesSelectedUser ? `(Filtrado por: ${queriesSelectedUser})` : ''}
                  </p>
                  <div onClick={(e) => {
                      const row = e.target.closest('.grid.items-center');
                      if(row) {
                          const dateText = row.children[0].title || row.children[0].textContent;
                          if (dateText !== '—' && dateText !== 'Data') setQueriesSelectedDate(dateText);
                      }
                  }} className="cursor-pointer">
                  <DataTable
                    sortKey={queriesDaySort.key}
                    sortDirection={queriesDaySort.dir}
                    onSort={(k) => handleSort(k, queriesDaySort, setQueriesDaySort)}
                    columns={[
                      { key: 'date', label: 'Data', width: '1fr' },
                      { key: 'queries', label: 'Consultas', width: '100px', right: true },
                    ]}
                    rows={sortData([...queriesByDay].map((d, i) => ({ _key: i, date: fmtDate(d.date), queries: d.queries })), queriesDaySort)}
                  />
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                    Por usuário {queriesSelectedDate ? `(Filtrado por: ${queriesSelectedDate})` : ''}
                  </p>
                  <div onClick={(e) => {
                      const row = e.target.closest('.grid.items-center');
                      if(row) {
                          const userText = row.children[0].title || row.children[0].textContent;
                          if (userText !== '—' && userText !== 'E-mail') setQueriesSelectedUser(userText);
                      }
                  }} className="cursor-pointer">
                  <DataTable
                    sortKey={queriesUserSort.key}
                    sortDirection={queriesUserSort.dir}
                    onSort={(k) => handleSort(k, queriesUserSort, setQueriesUserSort)}
                    columns={[
                      { key: 'email', label: 'E-mail', width: '2fr' },
                      { key: 'queries', label: 'Consultas', width: '100px', right: true },
                    ]}
                    rows={sortData([...queriesByUser].map((d, i) => ({ _key: i, email: d.email, queries: d.queries })), queriesUserSort)}
                  />
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
          )}

          {/* ── CONVERSAS ── */}
          {activeTab === 'conversas' && (
            <div className="space-y-4">
              <p className="text-sm text-on-surface-variant">Busca e auditoria de conversas de todos os usuários. Filtre, selecione e exporte.</p>

              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* User dropdown */}
                <div className="relative" ref={convDropRef}>
                  <label className="block text-[10px] font-bold uppercase text-on-surface-variant mb-1">Usuário</label>
                  <input
                    type="text"
                    placeholder={convSelectedUser ? '' : 'Todos — buscar...'}
                    value={convDropOpen ? convUserSearch : (convSelectedUser ? `${convSelectedUser.full_name || convSelectedUser.email}` : '')}
                    onChange={e => { setConvUserSearch(e.target.value); setConvDropOpen(true); }}
                    onFocus={() => setConvDropOpen(true)}
                    className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm"
                  />
                  {convSelectedUser && (
                    <button onClick={() => { setConvSelectedUser(null); setConvUserSearch(''); }} className="absolute right-2 top-7 text-on-surface-variant hover:text-on-surface text-xs" aria-label="Limpar">✕</button>
                  )}
                  {convDropOpen && (
                    <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border dark:border-outline bg-white  shadow-xl py-1">
                      <li>
                        <button className="w-full text-left px-3 py-2 text-sm hover:bg-surface-variant" onClick={() => { setConvSelectedUser(null); setConvDropOpen(false); setConvUserSearch(''); }}>Todos os usuários</button>
                      </li>
                      {convFilteredUsers.map(u => (
                        <li key={u.id}>
                          <button className="w-full text-left px-3 py-2 text-sm hover:bg-surface-variant truncate" onClick={() => { setConvSelectedUser(u); setConvDropOpen(false); setConvUserSearch(''); }}>
                            {u.full_name || u.email}
                          </button>
                        </li>
                      ))}
                      {convFilteredUsers.length === 0 && <li className="px-3 py-2 text-sm text-on-surface-variant">Nenhum encontrado</li>}
                    </ul>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-on-surface-variant mb-1">Data inicial</label>
                  <input type="date" value={convFrom} onChange={e => setConvFrom(e.target.value)} className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-on-surface-variant mb-1">Data final</label>
                  <input type="date" value={convTo} onChange={e => setConvTo(e.target.value)} className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase text-on-surface-variant mb-1">Conteúdo</label>
                  <input type="text" placeholder="Buscar no título ou texto..." value={convQuery} onChange={e => setConvQuery(e.target.value)} className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm" />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={loadConversas} disabled={convLoading} className="px-4 py-2 rounded-lg bg-primary text-on-primary dark:text-on-surface text-sm font-medium disabled:opacity-50">
                  {convLoading ? 'Buscando...' : 'Buscar'}
                </button>
                <button onClick={exportConvMD} disabled={convData.length === 0} className="px-4 py-2 rounded-lg border dark:border-outline text-sm font-medium hover:bg-surface-variant disabled:opacity-40">
                  Exportar MD
                </button>
                <button onClick={exportConvXLSX} disabled={convData.length === 0} className="px-4 py-2 rounded-lg border dark:border-outline text-sm font-medium hover:bg-surface-variant disabled:opacity-40">
                  Exportar XLSX
                </button>
                {convError && <span className="text-sm text-red-500">{convError}</span>}
                {convData.length > 0 && <span className="text-xs text-on-surface-variant ml-auto">{convData.length} mensagens</span>}
              </div>

              {/* Table */}
              <div className="rounded-xl border dark:border-outline overflow-hidden">
                <div className="grid grid-cols-12 bg-surface-container text-[10px] font-bold uppercase tracking-wider text-on-surface-variant px-3 py-2 gap-2 items-center">
                  <div className="col-span-1">
                    <input type="checkbox"
                      checked={convData.length > 0 && convSelected.size >= convData.length}
                      onChange={() => {
                        if (convSelected.size >= convData.length) setConvSelected(new Set());
                        else setConvSelected(new Set(convData.map(rowKey)));
                      }}
                    />
                  </div>
                  <div className="col-span-2 cursor-pointer hover:text-on-surface-variant dark:hover:text-slate-200" onClick={() => handleSort('user_name', convSort, setConvSort)}>Usuário {convSort.key==='user_name' ? (convSort.dir==='asc'?'↑':'↓') : ''}</div>
                  <div className="col-span-2 cursor-pointer hover:text-on-surface-variant dark:hover:text-slate-200" onClick={() => handleSort('conversation_title', convSort, setConvSort)}>Título conversa {convSort.key==='conversation_title' ? (convSort.dir==='asc'?'↑':'↓') : ''}</div>
                  <div className="col-span-1 cursor-pointer hover:text-on-surface-variant dark:hover:text-slate-200" onClick={() => handleSort('message_role', convSort, setConvSort)}>Tipo {convSort.key==='message_role' ? (convSort.dir==='asc'?'↑':'↓') : ''}</div>
                  <div className="col-span-4 cursor-pointer hover:text-on-surface-variant dark:hover:text-slate-200" onClick={() => handleSort('message_content', convSort, setConvSort)}>Conteúdo {convSort.key==='message_content' ? (convSort.dir==='asc'?'↑':'↓') : ''}</div>
                  <div className="col-span-2 cursor-pointer hover:text-on-surface-variant dark:hover:text-slate-200" onClick={() => handleSort('message_created_at', convSort, setConvSort)}>Data {convSort.key==='message_created_at' ? (convSort.dir==='asc'?'↑':'↓') : ''}</div>
                </div>
                <div className="divide-y dark:divide-slate-800 max-h-[55vh] overflow-y-auto">
                  {convData.length === 0 && !convLoading && (
                    <div className="px-3 py-8 text-sm text-on-surface-variant">Nenhum resultado. Ajuste os filtros e clique em Buscar.</div>
                  )}
                  {sortData(convData, convSort).map(row => {
                    const k = rowKey(row);
                    return (
                      <div key={k} className="grid grid-cols-12 text-xs px-3 py-2 gap-2 items-start hover:bg-surface-container dark:hover:bg-slate-800/50">
                        <div className="col-span-1 flex items-center pt-0.5">
                          <input type="checkbox" checked={convSelected.has(k)} onChange={() => setConvSelected(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; })} />
                        </div>
                        <div className="col-span-2 truncate text-on-surface-variant " title={row.user_email}>{row.user_name || row.user_email || '—'}</div>
                        <div className="col-span-2 truncate text-on-surface-variant " title={row.conversation_title}>{row.conversation_title || '—'}</div>
                        <div className="col-span-1">{row.message_role === 'user' ? 'Pergunta' : 'Resposta'}</div>
                        <div className="col-span-4 break-words max-h-12 overflow-y-auto text-on-surface-variant dark:text-on-surface-variant">{(row.message_content || '').slice(0, 180)}{(row.message_content || '').length > 180 ? '…' : ''}</div>
                        <div className="col-span-2 truncate text-on-surface-variant">{fmt(row.message_created_at)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── MAILING ── */}
          {activeTab === 'mailing' && (
            <div className="space-y-6">
                <h2 className="text-lg font-bold flex items-center gap-2 text-on-surface "><span className="material-symbols-outlined">mail</span> Envio de E-mails (Mailing)</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="rounded-2xl border dark:border-outline bg-surface p-6 space-y-4">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-on-surface-variant">Nova Campanha</h3>
                        <div>
                            <label className="block text-xs font-bold uppercase text-on-surface-variant mb-1">Assunto</label>
                            <input value={mailingSubject} onChange={e => setMailingSubject(e.target.value)} className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none" placeholder="Ex: Novidades no App" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase text-on-surface-variant mb-1">Público-alvo</label>
                            <select value={mailingTarget} onChange={e => setMailingTarget(e.target.value)} className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none">
                                <option value="all">Todos (exceto descadastrados)</option>
                                <option value="sem_plano">Apenas Gratuitos (sem plano ativo)</option>
                                <option value="premium">Apenas Plano Premium</option>
                                <option value="regular">Apenas Plano Regular</option>
                                <option value="specific">Usuários Específicos (E-mails)</option>
                            </select>
                        </div>
                        {mailingTarget === 'specific' && (
                            <div>
                                <label className="block text-xs font-bold uppercase text-on-surface-variant mb-1">E-mails (separados por vírgula)</label>
                                <textarea value={mailingEmails} onChange={e => setMailingEmails(e.target.value)} className="w-full rounded-lg border dark:border-outline px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none" placeholder="joao@email.com, maria@email.com" rows="2" />
                            </div>
                        )}
                        <div>
                            <label className="block text-xs font-bold uppercase text-on-surface-variant mb-1">Conteúdo (HTML)</label>
                            <textarea value={mailingContent} onChange={e => setMailingContent(e.target.value)} className="w-full rounded-lg border dark:border-outline  px-3 py-2 text-sm h-48 font-mono focus:border-primary focus:ring-1 focus:ring-primary outline-none" placeholder="<h1>Olá</h1>..." />
                        </div>
                        <button disabled={mailingLoading || !mailingSubject || !mailingContent || (mailingTarget === 'specific' && !mailingEmails.trim())} onClick={async () => {
                            if(!window.confirm('Confirma o envio?')) return;
                            setMailingLoading(true);
                            try {
                                const targetEmails = mailingTarget === 'specific' 
                                    ? mailingEmails.split(',').map(e => e.trim()).filter(Boolean)
                                    : undefined;
                                const res = await apiClient.sendAdminMailing({ payload: { subject: mailingSubject, content: mailingContent, target_plan: mailingTarget !== 'specific' ? mailingTarget : null, target_emails: targetEmails } });
                                if(res.res.ok) { 
                                    alert(res.data?.queued ? `Mailing enfileirado para ${res.data.audience_count} destinatário(s). O envio ocorre em segundo plano.` : (res.data?.message || 'Nenhum destinatário elegível.')); 
                                    setMailingSubject(''); 
                                    setMailingContent(''); 
                                    setMailingEmails('');
                                    loadMailingData(); 
                                }
                                else { alert('Erro ao enviar.'); }
                            } catch(e) { alert('Erro ao enviar.'); }
                            setMailingLoading(false);
                        }} className="px-4 py-3 rounded-xl bg-primary text-on-primary dark:text-on-surface text-sm font-medium w-full disabled:opacity-50 hover:opacity-90 transition-opacity">
                            {mailingLoading ? 'Enviando...' : 'Disparar Mailing'}
                        </button>
                    </div>
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-on-surface-variant">Histórico de Campanhas</h3>
                        <DataTable 
                            columns={[
                                { key: 'created_at', label: 'Data', width: '100px' },
                                { key: 'subject', label: 'Assunto', width: '1.5fr' },
                                { key: 'audience_count', label: 'Envios', width: '80px', right: true },
                                { key: 'target_plan', label: 'Alvo', width: '100px' }
                            ]}
                            rows={mailingCampaigns.map(c => ({
                                _key: c.id,
                                created_at: fmt(c.created_at),
                                subject: c.subject,
                                audience_count: c.audience_count,
                                target_plan: planLabel(c.target_plan || 'all')
                            }))}
                            emptyMsg="Nenhuma campanha enviada."
                        />
                    </div>
                </div>
            </div>
          )}

          {/* ── DENÚNCIAS ── */}
          {activeTab === 'denuncias' && <ContentReportsTab />}

          {/* ── OPTIN/OPTOUT ── */}
          {activeTab === 'optinout' && (
            <div className="space-y-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-lg font-bold flex items-center gap-2 text-on-surface "><span className="material-symbols-outlined">unsubscribe</span> Opt-in / Opt-out de Mailing</h2>
                        <p className="text-sm text-on-surface-variant mt-1">Controle de quais usuários decidiram sair da lista de marketing.</p>
                    </div>
                    <button onClick={loadMailingData} className="px-4 py-2 rounded-lg bg-surface-container-highest text-on-surface  text-xs font-medium hover:bg-slate-300 dark:hover:bg-slate-700 transition-colors">Atualizar lista</button>
                </div>
                {optinLoading ? (
                    <div className="flex items-center justify-center h-32 text-sm text-on-surface-variant gap-2">
                        <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
                        Carregando inscritos...
                    </div>
                ) : (
                    <DataTable 
                        sortKey={mailingSubSort.key}
                        sortDirection={mailingSubSort.dir}
                        onSort={(k) => handleSort(k, mailingSubSort, setMailingSubSort)}
                        columns={[
                            { key: 'email', label: 'E-mail', width: '2fr' },
                            { key: 'full_name', label: 'Nome', width: '1.5fr' },
                            { key: 'plan_id', label: 'Plano', width: '100px' },
                            { key: 'status', label: 'Status Mailing', width: '150px' },
                            { key: 'action', label: 'Ação', width: '100px', sortable: false, right: true }
                        ]}
                        rows={sortData(mailingSubscribers.map(s => ({
                            _key: s.id,
                            email: s.email,
                            full_name: s.full_name || '—',
                            plan_id: planLabel(s.plan_id),
                            status: s.unsubscribed_marketing ? <Badge text="Descadastrado (Opt-out)" color="red" /> : <Badge text="Ativo (Opt-in)" color="green" />,
                            action: (
                                <button onClick={async () => {
                                    const action = s.unsubscribed_marketing ? 'recadastrar' : 'descadastrar';
                                    if(!window.confirm(`Deseja ${action} ${s.email}?`)) return;
                                    const res = await apiClient.updateAdminMailingSubscriber({ userId: s.id, unsubscribed_marketing: !s.unsubscribed_marketing });
                                    if(res.res.ok) { loadMailingData(); }
                                    else { alert('Erro ao atualizar status.'); }
                                }} className="text-[10px] uppercase tracking-wider font-bold text-primary hover:underline">
                                    {s.unsubscribed_marketing ? 'Reativar' : 'Remover'}
                                </button>
                            )
                        })), mailingSubSort)}
                        emptyMsg="Nenhum registro encontrado."
                    />
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
